import { Carousel } from 'antd';
import { includes, isEmpty } from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { handleImageUrl } from '../../../common/helpers/sharpie/imageUrlHelper';
import LazyImage from '../../lazyImage/LazyImage';
import { CarouselSlider } from './CarouselSlider';
import { thumbnailImageConfig } from './config';
import {
  CarouselCounter,
  CarouselWrapper,
  ImageCaption,
} from './ContentCarousel.style';
import { getArticleTypeInGA } from '../../../common/analytics/helpers/articleTypeHelper';
import {
  trackArticleArrow,
  trackArticleThumbnail,
} from '../../../common/analytics/analyticsTracker';
import { WholeState } from '../../../store/common/type';
import { PostSubcategory } from '../../../common/helpers/domain/postCategories';
import { VideoType } from '../../../common/helpers/domain/videoTypes';

interface GalleryImage {
  ID: string;
  url: string;
  alt_text?: string;
  title?: string;
}
interface ContentCarouselProps {
  children: React.ReactNode;
  idGalleries: string[];
  source: string;
  totalItem: number;
  postingData: {
    postCategory: PostSubcategory;
    videoType?: VideoType;
  };
  lazyLoad: boolean;
  captions: string[];
  gallery: GalleryImage[];
}
const ContentCarousel: React.FC<ContentCarouselProps> = ({
  children,
  idGalleries,
  source,
  totalItem,
  postingData,
  lazyLoad,
  captions,
  gallery,
}) => {
  /**
   * @type {React.MutableRefObject<Carousel>}
   */
  const carouselRef = useRef(null);

  /**
   * @type {React.MutableRefObject<HTMLDivElement>}
   */
  const carouselWrapperRef = useRef(null);
  const [activeSlide, setActiveSlide] = useState(1);
  const deviceType = useSelector(
    (state: WholeState) => state.common.deviceType,
  );

  const isNextGenSupported = useSelector((state: WholeState) =>
    state.common !== undefined ? state.common.isNextGenSupported : false,
  );
  const { postCategory, videoType } = postingData || {};
  const articleTypeInGA = getArticleTypeInGA(postCategory, videoType);

  const userInfo = useSelector((state: WholeState) =>
    state !== undefined ? state.common.user : {},
  );

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (
        carouselWrapperRef.current?.contains(e.target) &&
        (includes(
          (e.target as HTMLElement).className,
          'slick-arrow slick-next',
        ) ||
          includes(
            (e.target as HTMLElement).className,
            'slick-arrow slick-prev',
          ))
      ) {
        trackArticleArrow(
          userInfo,
          {
            ...postingData,
            articleTypeInGA,
            id: 0,
            title: '',
            tags: [],
          },
          source,
        );
      }
    };

    window.addEventListener('click', handleClick, false);
    // returned function will be called on component unmount
    return () => {
      window.removeEventListener('click', handleClick, false);
    };
  }, [userInfo, postingData, source, carouselWrapperRef, articleTypeInGA]);

  const setButtonAndSlideStates = (slide: number) => {
    const currentSlide = slide + 1;
    setActiveSlide(currentSlide);
  };

  const handleBeforeChange = (oldIndex: number, newIndex: number) => {
    // Update button and slide states
    setButtonAndSlideStates(newIndex);
  };

  const getCarouselThumbnailImages = (
    idGalleries: string[],
    gallery: GalleryImage[],
  ) => {
    return idGalleries.map((id) => {
      const data: GalleryImage = gallery.find(
        (image: GalleryImage) => image.ID === id,
      );
      const imageInfo = thumbnailImageConfig[deviceType];
      let dataUrl = '';
      let dataAltText = '';
      let dataId = 0;
      let dataTitle = '';
      let imgAltText = '';
      if (!isEmpty(data)) {
        dataId = parseInt(data.ID);
        dataUrl = data.url;
        dataAltText = data.alt_text;
        dataTitle = data.title;
        imgAltText = Object.prototype.hasOwnProperty.call(data, 'alt_text')
          ? dataAltText
          : '';
      }

      const sharpieImgUri = handleImageUrl(
        dataUrl,
        imageInfo.width,
        imageInfo.height,
        isNextGenSupported,
      );

      return (
        <LazyImage
          key={dataId}
          alt={imgAltText}
          src={sharpieImgUri}
          onClick={() =>
            trackArticleThumbnail(
              userInfo,
              {
                ...postingData,
                articleTypeInGA,
              },
              source,
              dataTitle,
            )
          }
          lazyLoad={lazyLoad}
          height={`${imageInfo.height}px`}
        />
      );
    });
  };

  const renderCarousel = (
    idGalleries: string[],
    gallery: GalleryImage[],
    activeSlide: number,
    totalItem: number,
  ) => {
    const settings = {
      dots: false,
      speed: 600,
      arrows: true,
      swipeToSlide: false,
      infinite: false,
      adaptiveHeight: true,
      slidesToShow: 1,
      slidesToScroll: 1,
      variableWidth: false,
      beforeChange: handleBeforeChange,
    };

    return (
      <div className="carousel" ref={carouselWrapperRef}>
        <Carousel ref={carouselRef} {...settings}>
          {children}
        </Carousel>
        <CarouselCounter>
          {activeSlide}/{totalItem}
        </CarouselCounter>
      </div>
    );
  };

  return (
    <CarouselWrapper data-test={'content-carousel'}>
      {idGalleries &&
        gallery &&
        renderCarousel(idGalleries, gallery, activeSlide, totalItem)}
      {idGalleries && gallery && (
        <CarouselSlider
          images={getCarouselThumbnailImages(idGalleries, gallery)}
          selectedImageIndex={activeSlide}
          onClick={(imageIndex) => {
            setActiveSlide(imageIndex);
            carouselRef.current.goTo(imageIndex - 1);
          }}
        />
      )}
      <ImageCaption>
        <p>{captions[activeSlide - 1]}</p>
      </ImageCaption>
    </CarouselWrapper>
  );
};

export default ContentCarousel;
